import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, RotateCcw } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const VOICEFLOW_API_KEY = 'VF.DM.69c047d26de82a25dadd1d7c.NOSgpuFQOsHY6etO';
const VOICEFLOW_RUNTIME_URL = 'https://general-runtime.voiceflow.com';

export const ChatAgent = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Translations
  const t = {
    fr: {
      title: 'Agent IA',
      subtitle: 'Comment puis-je vous aider?',
      placeholder: 'Écrivez votre message...',
      send: 'Envoyer',
      newChat: 'Nouvelle conversation',
      thinking: 'En train de réfléchir...',
      error: 'Erreur de connexion. Réessayez.',
      welcome: 'Bonjour! Je suis l\'assistant virtuel de NgSys. Comment puis-je vous aider aujourd\'hui?'
    },
    en: {
      title: 'AI Agent',
      subtitle: 'How can I help you?',
      placeholder: 'Type your message...',
      send: 'Send',
      newChat: 'New conversation',
      thinking: 'Thinking...',
      error: 'Connection error. Please try again.',
      welcome: 'Hello! I\'m the NgSys virtual assistant. How can I help you today?'
    }
  };

  const text = t[language] || t.fr;

  // Generate session ID
  useEffect(() => {
    const storedSessionId = sessionStorage.getItem('voiceflow_session');
    if (storedSessionId) {
      setSessionId(storedSessionId);
    } else {
      const newSessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('voiceflow_session', newSessionId);
      setSessionId(newSessionId);
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Send welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0 && sessionId) {
      launchConversation();
    }
  }, [isOpen, sessionId]);

  const launchConversation = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${VOICEFLOW_RUNTIME_URL}/state/user/${sessionId}/interact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': VOICEFLOW_API_KEY,
        },
        body: JSON.stringify({
          action: {
            type: 'launch',
            payload: {
              language: language
            }
          },
          config: {
            tts: false,
            stripSSML: true,
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        processVoiceflowResponse(data);
      } else {
        // Fallback to local welcome message
        setMessages([{
          type: 'bot',
          content: text.welcome,
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Voiceflow launch error:', error);
      setMessages([{
        type: 'bot',
        content: text.welcome,
        timestamp: new Date()
      }]);
    }
    setIsLoading(false);
  };

  const processVoiceflowResponse = (traces) => {
    const botMessages = [];
    
    traces.forEach(trace => {
      if (trace.type === 'text' || trace.type === 'speak') {
        botMessages.push({
          type: 'bot',
          content: trace.payload?.message || trace.payload?.text || '',
          timestamp: new Date()
        });
      } else if (trace.type === 'visual' && trace.payload?.image) {
        botMessages.push({
          type: 'bot',
          content: '',
          image: trace.payload.image,
          timestamp: new Date()
        });
      }
    });

    if (botMessages.length > 0) {
      setMessages(prev => [...prev, ...botMessages]);
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || !sessionId) return;

    // Add user message
    const userMessage = {
      type: 'user',
      content: messageText,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${VOICEFLOW_RUNTIME_URL}/state/user/${sessionId}/interact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': VOICEFLOW_API_KEY,
        },
        body: JSON.stringify({
          action: {
            type: 'text',
            payload: messageText
          },
          config: {
            tts: false,
            stripSSML: true,
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        processVoiceflowResponse(data);
      } else {
        setMessages(prev => [...prev, {
          type: 'bot',
          content: text.error,
          timestamp: new Date(),
          isError: true
        }]);
      }
    } catch (error) {
      console.error('Voiceflow error:', error);
      setMessages(prev => [...prev, {
        type: 'bot',
        content: text.error,
        timestamp: new Date(),
        isError: true
      }]);
    }
    setIsLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const resetConversation = async () => {
    // Generate new session
    const newSessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('voiceflow_session', newSessionId);
    setSessionId(newSessionId);
    setMessages([]);
    
    // Will trigger launchConversation via useEffect
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div 
          className={cn(
            "fixed rounded-2xl shadow-2xl flex flex-col overflow-hidden border",
            // Responsive positioning with margins
            "bottom-2 right-2 left-2 top-auto h-[calc(100vh-80px)] max-h-[600px]", // Mobile: 8px margins
            "sm:bottom-4 sm:right-4 sm:left-auto sm:top-auto sm:w-[380px] sm:h-[500px]", // Tablet: 16px margins
            "lg:bottom-6 lg:right-6 lg:w-[400px] lg:h-[550px]", // Desktop: 24px margins
            "z-[9999]",
            theme === 'dark' 
              ? "bg-gray-900 border-gray-700" 
              : "bg-white border-gray-200"
          )}
          data-testid="chat-window"
        >
          {/* Header */}
          <div className={cn(
            "flex items-center justify-between px-4 py-3 border-b",
            theme === 'dark'
              ? "bg-primary border-gray-700"
              : "bg-primary"
          )}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">{text.title}</h3>
                <p className="text-xs text-white/70">{text.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={resetConversation}
                className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                title={text.newChat}
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleChat}
                className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div className={cn(
            "flex-1 overflow-y-auto p-4",
            theme === 'dark' ? "bg-gray-900" : "bg-gray-50"
          )}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex gap-3 mb-6",
                  msg.type === 'user' ? "justify-end" : "justify-start"
                )}
              >
                {/* Bot Avatar */}
                {msg.type === 'bot' && (
                  <div className={cn(
                    "flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm",
                    "bg-primary"
                  )}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7h1a1 1 0 011 1v3a1 1 0 01-1 1h-1v1a2 2 0 01-2 2H5a2 2 0 01-2-2v-1H2a1 1 0 01-1-1v-3a1 1 0 011-1h1a7 7 0 017-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 012-2zM7.5 13a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm9 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM12 9a5 5 0 00-5 5v1h10v-1a5 5 0 00-5-5z"/>
                    </svg>
                  </div>
                )}
                
                <div
                  className={cn(
                    "max-w-[75%] rounded-2xl px-4 py-3 text-sm shadow-sm",
                    msg.type === 'user'
                      ? "bg-primary text-white rounded-br-sm"
                      : theme === 'dark'
                        ? "bg-gray-800 text-gray-100 rounded-bl-sm border border-gray-700"
                        : "bg-white text-gray-800 rounded-bl-sm border border-gray-100",
                    msg.isError && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200"
                  )}
                >
                  {msg.image && (
                    <img src={msg.image} alt="" className="rounded-lg mb-2 max-w-full" />
                  )}
                  {msg.content && <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>}
                </div>
                
                {/* User Avatar */}
                {msg.type === 'user' && (
                  <div className={cn(
                    "flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm",
                    "bg-accent"
                  )}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex gap-3 justify-start mb-6">
                {/* Bot Avatar for loading */}
                <div className={cn(
                  "flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm",
                  "bg-primary"
                )}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7h1a1 1 0 011 1v3a1 1 0 01-1 1h-1v1a2 2 0 01-2 2H5a2 2 0 01-2-2v-1H2a1 1 0 01-1-1v-3a1 1 0 011-1h1a7 7 0 017-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 012-2zM7.5 13a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm9 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM12 9a5 5 0 00-5 5v1h10v-1a5 5 0 00-5-5z"/>
                  </svg>
                </div>
                <div className={cn(
                  "rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm",
                  theme === 'dark' ? "bg-gray-800 border border-gray-700" : "bg-white border border-gray-100"
                )}>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form 
            onSubmit={handleSubmit}
            className={cn(
              "p-3 border-t",
              theme === 'dark' ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
            )}
          >
            <div className={cn(
              "flex items-center gap-2 rounded-full px-4 py-2",
              theme === 'dark' ? "bg-gray-800" : "bg-gray-100"
            )}>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={text.placeholder}
                disabled={isLoading}
                className={cn(
                  "flex-1 bg-transparent text-sm outline-none",
                  theme === 'dark' ? "text-white placeholder:text-gray-500" : "text-gray-800 placeholder:text-gray-400"
                )}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                size="icon"
                disabled={!inputValue.trim() || isLoading}
                className="h-8 w-8 rounded-full bg-primary hover:bg-primary/90"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={toggleChat}
        style={{ 
          position: 'fixed',
          bottom: '16px',
          right: '16px',
          zIndex: 9998,
        }}
        className={cn(
          "flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium shadow-lg backdrop-blur-sm transition-all duration-300 hover:scale-105",
          theme === 'dark'
            ? "bg-accent/90 hover:bg-accent text-white"
            : "bg-accent/80 hover:bg-accent text-white"
        )}
        data-testid="chat-toggle-button"
        aria-label={isOpen ? (language === 'fr' ? 'Fermer le chat' : 'Close chat') : (language === 'fr' ? 'Ouvrir le chat' : 'Open chat')}
      >
        {isOpen ? (
          <X className="w-4 h-4" />
        ) : (
          <MessageCircle className="w-4 h-4" />
        )}
        <span>{language === 'fr' ? 'Agent IA' : 'AI Agent'}</span>
      </button>
    </>
  );
};
